
import React, { Fragment, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';

// Components

import ErrorMessage from '@components/common/alerts/ErrorMessage';

// Utils

import { convertSizeValueToString } from '@utils/miscellaneous/convertSizeValueToString';
import { noop } from '@utils/miscellaneous/objectHelpers';
import { findEncryptedDocumentPath } from '@utils/miscellaneous/findEncryptedDocumentPath';

// Constants

import { UPLOAD_MODAL_VIEWS } from '@constants/common/miscellaneous';

const UploadView = props => {
    const {
        acceptedFileTypes,
        excludeFileExtensionList,
        acceptedFileTypesValidation,
        acceptedFileTypesVisualization,
        isAnyError,
        maxFileSize,
        maxNameLength,
        fileNameValidator,
        parentActions: {
            handleResponse,
            setFileName,
            setFileSize,
            setEncryptedDocumentPath,
            toggleBackEndErrorVisibility,
            toggleSameUploadVisibility,
            toggleView,
            uploadAction,
        },
        primaryTopAnchor,
        rejectedDocuments,
        sameFileUploadCheckEnabled,
        uploadPayloadComponents,
        sizeLimit
    } = props;

    const NOT_SUPPORTED_EXTENSION = 'NOT_SUPPORTED_EXTENSION';
    const FILE_SIZE_LIMIT = 'FILE_SIZE_LIMIT';
    const FILE_NAME_LENGTH_LIMIT = 'FILE_NAME_LENGTH_LIMIT';
    const FILE_NAME_ILLEGAL_CHARS = 'FILE_NAME_ILLEGAL_CHARS';

    const UPLOAD_ERRORS_MESSAGES = {
        [NOT_SUPPORTED_EXTENSION]: {
            header: 'modals.upload_document.error_header',
            message: 'modals.upload_document.error_message.extensions',
            errorMessageReplacements: { allowedExtensions: acceptedFileTypesVisualization }
        },
        [FILE_SIZE_LIMIT]: {
            header: 'modals.upload_document.error_header',
            message: 'modals.upload_document.error_message.file_size',
            errorMessageReplacements: { sizeLimit }
        },
        [FILE_NAME_LENGTH_LIMIT]: {
            header: 'modals.upload_document.error_header',
            message: 'modals.upload_document.error_message.file_name_length',
            errorMessageReplacements: { maxNameLength }
        },
        [FILE_NAME_ILLEGAL_CHARS]: {
            header: 'modals.upload_document.error_header',
            message: 'modals.upload_document.error_message.illegal_chars',
            additionalContent: (
                <ul>
                    <li className = 'text-left margin-left-2'>
                        <p className = 'margin-top-2px margin-bottom-2px'>
                            <FormattedMessage id = 'modals.upload_document.error_message.alphanumeric'/>
                        </p>
                    </li>
                    <li  className = 'text-left margin-left-2'>
                        <p className = 'margin-top-2px margin-bottom-2px'>
                            @ . , -
                        </p>
                    </li>
                </ul>
            )
        }
    };

    const INITIAL_WARNING = {
        header: '',
        message: '',
        errorMessageReplacements: null,
        additionalContent: null
    };

    const [warning, updateWarning] = useState(INITIAL_WARNING);

    const labelRef = useRef(null);

    const toggleTextareaHoveringState = ({ currentTarget }, state) =>
        currentTarget && currentTarget.classList[state ? 'add' : 'remove']('drag_and_drop_area--hovered');

    const changeWarning = type => {
        if (UPLOAD_ERRORS_MESSAGES[type]) {
            updateWarning(UPLOAD_ERRORS_MESSAGES[type]);
        } else {
            updateWarning(INITIAL_WARNING);
        }
    };

    const handleUploadClick = (e, passTheSame = false) => {

        e.persist();

        if(isAnyError) {
            return;
        }

        const {
            files
        } = e.target;

        const [ file ] = files;

        const {
            name,
            size,
            type: dataType
        } = file;

        // Validate against the file's actual extension rather than the browser-reported
        // MIME type (file.type).
        const getFileExtension = fileName => {
            const parts = fileName.split('.');
            return parts.length > 1 ? parts.pop().toLowerCase() : '';
        };
        const fileExtension = getFileExtension(name);
        const isAcceptedFileType = acceptedFileTypesValidation.some(type => type.toLowerCase() === fileExtension);

        if (!isAcceptedFileType || excludeFileExtensionList?.includes(fileExtension)) {
            e.target.value = null;
            changeWarning(NOT_SUPPORTED_EXTENSION);
            return;
        }

        if(size > maxFileSize) {
            e.target.value = null;
            changeWarning(FILE_SIZE_LIMIT);
            return;
        }

        if(name.length > maxNameLength) {
            e.target.value = null;
            changeWarning(FILE_NAME_LENGTH_LIMIT);
            return;
        }

        if(!(typeof fileNameValidator === 'function' && fileNameValidator(name))) {
            e.target.value = null;
            changeWarning(FILE_NAME_ILLEGAL_CHARS);
            return;
        }

        const sameUploadFileName = rejectedDocuments.reduce((res, { uploadedFileName, uploadedFileSize }) => {
            if(res) {
                return res;
            } else {
                return uploadedFileName === name || +uploadedFileSize === +size ? uploadedFileName : '';
            }
        }, '');

        if(sameFileUploadCheckEnabled){
            if(sameUploadFileName && !passTheSame) {
                toggleSameUploadVisibility({
                    sameUploadFileName,
                    continueAction: () => {
                        toggleSameUploadVisibility(false);
                        handleUploadClick.apply(this, [ e, true ]);
                    },
                    closeAction: () => {
                        e.target.value = null;
                        toggleTextareaHoveringState(e, false);
                    }
                });
                return;
            }
        }

        const formDataPayloadConfig = {
            ...uploadPayloadComponents,
            dataType,
            file,
            name,
            size
        };

        const composeFormData = () => {
            const formData = new FormData();
            Object.entries(formDataPayloadConfig).map(([ prop, value ]: [string, string ]) => {
                formData.append(prop, value);
            });
            return formData;
        };

        const formData = composeFormData();
        e.target.value = null;

        const { documentType, applicantEventId, categoryType } = uploadPayloadComponents;
    
        uploadAction(formData)
            .then(res => handleResponse(
                res,
                () => {
                    const encryptedDocumentPath = findEncryptedDocumentPath({
                        res,
                        applicantEventId,
                        documentType,
                        uploadedFileName: name,
                        uploadedFileSize: size,
                        categoryType,
                    });
                    if (encryptedDocumentPath) {
                        setEncryptedDocumentPath(encryptedDocumentPath);
                        toggleView(UPLOAD_MODAL_VIEWS.REMOVE_DOCUMENT);
                        setFileName(name);
                        setFileSize(convertSizeValueToString(size));
                        primaryTopAnchor.current.focus();
                        toggleTextareaHoveringState(e, false);
                        warning && changeWarning(INITIAL_WARNING);
                    } else {
                        toggleBackEndErrorVisibility('modals.upload_document.missing_path');
                    }
                },
                message => toggleBackEndErrorVisibility(message)
            ));
    };

    const handleBrowseClick = e => {
        e.preventDefault();
        if(isAnyError) {
            return null;
        }
        // @ts-ignore
        labelRef.current.click();
    };

    return (
        <Fragment>
            <input
                id = 'upload-document-input'
                type = 'file'
                hidden
                accept = { acceptedFileTypes }
                onChange = { handleUploadClick }
            />
            <div
                id = 'upload-view-file-info'
                className = {`file-upload-area file-upload-area--upload-view${warning.message ? '--with-error' : ''}`}
                onDragOver = { e => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleTextareaHoveringState(e, true);
                } }
                onDragLeave = { e => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleTextareaHoveringState(e, false);
                } }
                onDrop = { e => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleUploadClick({
                        persist: noop,
                        target: {
                            files: e.dataTransfer.files,

                        },
                        currentTarget: e.currentTarget
                    });
                } }
            >
                <div className = { `file-upload-area__contents${warning.message ? '--with-error' : ''}` }>
                    <div className = 'file-upload-area__upload-graphic'>
                        <FontAwesomeIcon
                            icon = 'cloud-upload-alt'
                            size = '3x'
                        />
                    </div>
                    <div className = 'file-upload-area__upload-text'>
                        <FormattedMessage id = 'modals.drag_and_drop_here' />
                    </div>
                    <div className = 'file-upload-area__upload-or'>
                        <FormattedMessage id = 'common.or' />
                    </div>
                    <label
                        className = 'file-upload-area__browse-files'
                        ref = { labelRef }
                        htmlFor = 'upload-document-input'
                    >
                        <a
                            href='#'
                            onClick = { handleBrowseClick }
                        >
                            <FormattedMessage id = 'modals.browser_files' />
                        </a>
                    </label>
                </div>
                {
                    warning.message &&

                    <div className = 'error-message-wrapper position-absolute bottom-2' >
                        <ErrorMessage
                            {
                                ...{
                                    errorMessage: warning.message,
                                    header: warning.header,
                                    id: warning.message,
                                    errorMessageReplacements: warning.errorMessageReplacements,
                                    parentAction:{
                                        closeError: changeWarning
                                    },
                                    additionalContent: warning.additionalContent
                                }
                            }
                        />
                    </div>
                }
            </div>
        </Fragment>
    );
};

export default UploadView;